import useQuery from 'app/hooks/use-query'
import useRequester from 'app/hooks/use-requester'
import { rateContentMutation } from 'app/mutations/content/rate'
import { favouriteMutation } from 'app/mutations/content/toggle-favourite'
import { watchLaterMutation } from 'app/mutations/content/toggle-watch-later'
import {
  createContentPreviewQuery,
  createContentQuery,
  mapToPreviewContent,
  mapToContent,
  Content,
} from 'app/queries/content/content'
import { useStores } from 'app/store'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

const standardQuery = createContentQuery()
const previewQuery = createContentPreviewQuery()
const favourite = favouriteMutation()
const watchLater = watchLaterMutation()
const rate = rateContentMutation()

interface ContentOptions {
  id?: string
  slug: string
  preview?: boolean
  token?: string
}

interface ContentHookStateParams {
  id?: string
  slug: string
}

export default function useContent(options: ContentOptions) {
  const { lastCollectionStore } = useStores()
  const queryClient = useQueryClient()

  const { request } = useRequester()

  const [params, setParams] = useState<ContentHookStateParams>({
    id: options.id,
    slug: options.slug,
  })

  useEffect(() => {
    setParams({ slug: options.slug, id: options.id })
  }, [options.slug])

  const variables = useMemo(() => {
    const vars: any = { slug: params?.slug, id: params?.id }

    if (
      !lastCollectionStore ||
      !lastCollectionStore?.collectionId ||
      lastCollectionStore?.isPrimaryCollection
    ) {
      return vars
    }

    const { collectionId, collectionType } = lastCollectionStore

    if (collectionType === 'Collection') {
      vars.collection = collectionId
    } else if (collectionType === 'Tag') {
      vars.tag = collectionId
    } else if (collectionType === 'MemberTier') {
      vars.memberTier = collectionId
    }

    return vars
  }, [params.slug, lastCollectionStore])

  const query = useMemo(() => {
    if (options.preview) {
      return previewQuery
    }

    return standardQuery
  }, [options.preview])

  const { loading, data, error, fetchData } = useQuery(
    query,
    variables,
    options.preview ? mapToPreviewContent : mapToContent,
    {
      refetchOnMount: 'always',
      staleTime: Infinity,
      jwtToken: options.token,
    },
  )

  const toggleFavourite = async () => {
    if (!data) return

    const queryKey = ['query', query, variables]
    const previousData = queryClient.getQueryData<Content>(queryKey)

    queryClient.setQueryData<Content | undefined>(queryKey, (oldData) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        isFavourited: !oldData.isFavourited,
        totalFavourites: oldData.isFavourited
          ? Math.max(0, oldData.totalFavourites - 1)
          : oldData.totalFavourites + 1,
      }
    })

    try {
      const result = await request(favourite, { id: data.id })
      queryClient.invalidateQueries('favorites')
      return result
    } catch (error) {
      if (previousData) {
        queryClient.setQueryData(queryKey, previousData)
      }
      throw error
    }
  }

  const toggleWatchLater = async () => {
    if (!data) return

    const queryKey = ['query', query, variables]
    const previousData = queryClient.getQueryData<Content>(queryKey)

    queryClient.setQueryData<Content | undefined>(queryKey, (oldData) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        isInWatchLater: !oldData.isInWatchLater,
      }
    })

    try {
      const result = await request(watchLater, { id: data.id })
      queryClient.invalidateQueries('watch-later')
      queryClient.invalidateQueries('watch-later-batch')
      return result
    } catch (error) {
      if (previousData) {
        queryClient.setQueryData(queryKey, previousData)
      }
      throw error
    }
  }

  const rateContent = async (rating: number) => {
    const result = await request(rate, {
      id: data?.id,
      rating,
    })
    await fetchData()
    return result
  }

  useEffect(() => {
    if (lastCollectionStore && !lastCollectionStore.collectionId && data) {
      lastCollectionStore.setIsPrimaryCollection(true)
    }
  }, [data, lastCollectionStore])

  return {
    loading,
    data,
    error,
    fetchData,
    setParams,
    collectionName: lastCollectionStore?.collectionName,
    collectionId: lastCollectionStore?.collectionId,
    toggleFavourite,
    toggleWatchLater,
    rateContent,
  }
}
